import type { SerializedEditorState } from 'lexical';

/**
 * Simplified synchronous version for basic use cases.
 * Creates a minimal Lexical state structure directly without using the editor.
 *
 * This is faster but less robust than the headless editor approach.
 * Use plainTextToLexical() for production code.
 */
export const plainTextToLexicalSync = (plainText: string | null): SerializedEditorState | null => {
    if (!plainText?.trim()) {
        return null;
    }

    try {
        // Split by line breaks and filter empty lines at start/end
        const lines = plainText.split(/\r?\n/).filter((line, index, arr) => {
            if (line.trim()) return true;
            // Keep empty lines that are between non-empty lines
            const hasContentBefore = arr.slice(0, index).some((l) => l.trim());
            const hasContentAfter = arr.slice(index + 1).some((l) => l.trim());
            return hasContentBefore && hasContentAfter;
        });

        if (lines.length === 0) {
            return null;
        }

        // Create Lexical JSON structure manually
        const children = lines.map((line) => ({
            children: line.trim()
                ? [
                      {
                          detail: 0,
                          format: 0,
                          mode: 'normal' as const,
                          style: '',
                          text: line,
                          type: 'text' as const,
                          version: 1,
                      },
                  ]
                : [],
            direction: 'ltr' as const,
            format: '',
            indent: 0,
            type: 'paragraph' as const,
            version: 1,
        }));

        return {
            root: {
                children,
                direction: 'ltr' as const,
                format: '',
                indent: 0,
                type: 'root' as const,
                version: 1,
            },
        };
    } catch (error) {
        console.error('Error in synchronous plain text to Lexical conversion:', error);
        return null;
    }
};
